/**
 * Display vocabulary for the quote book — phase pills, dates, and the
 * wireframe's money style.
 *
 * Money and date helpers are re-exported from the order book rather than
 * duplicated: the two screens are the same table style and must not drift.
 */
import type { StatusTone } from "@flint/ui";

export { orderMoney as quoteMoney, exactMoney, shortDate } from "../orders/order-format.ts";

/**
 * What the badge says. These are **phases**, not stored statuses — `expiring`
 * and `expired` are both `sent` rows either side of the expiry cutoff.
 */
export const QUOTE_PHASE_LABEL = {
  draft: "Draft",
  pending: "Sent",
  expiring: "Expiring soon",
  expired: "Expired",
  converted: "Converted",
} as const;

/**
 * Tones follow the deck: sent is informational (`b-info` → `sky`, since
 * `StatusTone` has no `info`), expiring amber because it needs acting on this
 * week, expired red, converted green, draft muted.
 */
export const QUOTE_PHASE_TONE = {
  draft: "muted",
  pending: "sky",
  expiring: "warn",
  expired: "bad",
  converted: "ok",
} as const satisfies Record<string, StatusTone>;

export type QuotePhase = keyof typeof QUOTE_PHASE_LABEL;

/**
 * The detail header's long form — the deck writes "Expiring in 4 days" there
 * but only "Expiring 4d" in the table, so the pill can't just reuse the label.
 * Falls back to the plain label whenever a day count would read oddly.
 */
export function expiryLabel(phase: QuotePhase, expiresOn: string | null, now: Date): string {
  if (phase !== "expiring" || !expiresOn) return QUOTE_PHASE_LABEL[phase];
  const days = Math.round(
    (new Date(`${expiresOn}T00:00:00Z`).getTime() -
      new Date(now.toISOString().slice(0, 10) + "T00:00:00Z").getTime()) /
      86_400_000,
  );
  if (days <= 0) return "Expires today";
  return days === 1 ? "Expiring in 1 day" : `Expiring in ${days} days`;
}
